// Basic
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
//  Libs
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useSessionStore from '@/app/store/session';
import actionsOrganizations from '../../../api/actionsOrganizations';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import FooterForm from '@/common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import Select from '@/common-utils/components/ui/select';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
import { Input } from '@/common-utils/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
// Common
import organizationFields from '../../../fields/organizationFields';
import OrganizationType from '../../../enums/OrganizationTypes';
import HiddenPermissions from '@/app/permissions/HiddenPermissions';
import getObjectAttributeFromStringKey from '@/common-utils/functions/getObjectAttributeFromStringKey';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import BillableOptions from '@/app/modules/organizations/enums/BillableOptions';
import buildDictionary from '@/common-utils/functions/buildDictionary';
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.organizationDetails
 * @param root0.setLaunchRefetch
 * @returns ModalUpdateOrganization
 */
function ModalUpdateOrganization({ isOpen, setIsOpen, organizationDetails, setLaunchRefetch }) {
    // Constants
    const { uuid: organizationId, legalName, clientCode, legalInfos, registrationNumber, billingId, billable, comment } = organizationDetails ?? {};
    const billableOptions = buildDictionary(BillableOptions, 'entity', 'name');


    // Toast
    const { toast } = useToast();

    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const { permissions: userPermissions } = useSessionStore(s => s.session) ?? {};

    // Functions
    const isRootOrganization = () => {
        return organizationDetails && organizationDetails[organizationFields.UUID.key] === rootOrganization.uuid;
    };

    const isOfType = (...organizationTypes) => {
        return organizationDetails && organizationTypes.some(organizationType => organizationDetails[organizationFields.TYPE.key] === organizationType.name);
    };

    const isOrganizationBillable = () => {
        const isBillable = getObjectAttributeFromStringKey(organizationDetails, organizationFields.BILLABLE.key);
        return organizationDetails ? (isBillable !== null && isBillable !== undefined) : false;
    };

    // Permissions
    const hasBillableUpdatePermissions = () => {
        return hasRequiredPermissions({
            permissions: userPermissions,
            requiredPermissions: [HiddenPermissions.BILLABILITY_UPDATE.name]
        });
    };

    const hasBillingIdReadPermissions = () => {
        return hasRequiredPermissions({
            permissions: userPermissions,
            requiredPermissions: [HiddenPermissions.BILLING_ID_READ.name]
        });
    };

    const hasBillingIdUpdatePermissions = () => {
        return hasRequiredPermissions({
            permissions: userPermissions,
            requiredPermissions: [HiddenPermissions.BILLING_ID_UPDATE.name]
        });
    };

    const getBillingIdUpdateField = () => {
        const billingIdValue = organizationDetails[organizationFields.BILLING_ID_READ.key] ?? null;
        return hasBillingIdUpdatePermissions() && (billingIdValue === null || billingIdValue === undefined);
    };

    // Constants
    const showLegalNameField = !isRootOrganization();
    const showBusinessNameAndRegistrationField = !isRootOrganization() && isOfType(OrganizationType.WHITE_LABEL);
    const showBillableField = hasBillableUpdatePermissions() && isOrganizationBillable() && isOfType(OrganizationType.WHITE_LABEL);
    const showBillingIdField = hasBillingIdReadPermissions();

    // Update organization form
    const udpateOrganizationFormSchema = z.object({
        clientCode: z.string().optional(),
        comment: z.string().optional(),
        ...showLegalNameField ? { legalName: z.string().nonempty() } : {},
        ...showBusinessNameAndRegistrationField ? {
            businessName: z.string().optional(),
            registrationNumber: z.string().nonempty()
        } : {},
        ...showBillableField ? { billable: z.boolean() } : {},
        ...showBillingIdField ? { billingId: z.string().optional() } : {}
    });
    const updateOrganizationForm = useForm({
        resolver: zodResolver(udpateOrganizationFormSchema),
        mode: 'onChange',
        defaultValues: {
            clientCode: clientCode ?? '',
            comment: comment ?? '',
            ...showLegalNameField ? { legalName: legalName ?? '' } : {},
            ...showBusinessNameAndRegistrationField ? {
                businessName: legalInfos?.businessName ?? '',
                registrationNumber: registrationNumber ?? ''
            } : {},
            ...showBillableField ? { billable: billable } : {},
            ...showBillingIdField ? { billingId: billingId ?? '' } : {}
        }
    });

    // API Calls
    const onSuccess = () => {
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity='modal.update_organization.success' namespace={nsOrganization} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);  // Close modal even on error
        toast({
            description: <Entity entity='modal.update_organization.error.default' namespace={nsOrganization} />,
            variant: 'danger'
        });
    };
    const updateOrganization = actionsOrganizations.updateOrganization(onSuccess, onError);

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className='max-w-2xl'>
                <HeaderTitle title='modal.update_organization.title' namespace={nsOrganization} />
                <ModalBody>
                    <Form {...updateOrganizationForm}  >
                        <div className='space-y-2'>
                            {showLegalNameField &&
                                <FormField
                                    name='legalName'
                                    control={updateOrganizationForm.control}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel required={showLegalNameField}>
                                                <Entity namespace={nsOrganization} entity='keys.legal_name' />
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type='text'
                                                    autoComplete='off'
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            }
                            {showBusinessNameAndRegistrationField &&
                                <>
                                    <FormField
                                        name='businessName'
                                        control={updateOrganizationForm.control}
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>
                                                    <Entity namespace={nsOrganization} entity='keys.business_name' />
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        type='text'
                                                        autoComplete='off'
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <FormField
                                        name='registrationNumber'
                                        control={updateOrganizationForm.control}
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel required={showBusinessNameAndRegistrationField}>
                                                    <Entity namespace={nsOrganization} entity='keys.registration_number' />
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        type='text'
                                                        autoComplete='off'
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </>
                            }
                            <FormField
                                name='clientCode'
                                control={updateOrganizationForm.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            <Entity namespace={nsOrganization} entity='keys.client_code' />
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                type='text'
                                                autoComplete='off'
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            {showBillableField &&
                                <FormField
                                    name='billable'
                                    control={updateOrganizationForm.control}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel required={showBillableField}>
                                                <Entity namespace={nsOrganization} entity='keys.billable' />
                                            </FormLabel>
                                            <FormControl>
                                                <Select
                                                    {...field}
                                                    options={billableOptions}
                                                    namespace={nsOrganization}
                                                    type='text'
                                                    autoComplete='off'
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            }
                            {showBillingIdField &&
                                <FormField
                                    name='billingId'
                                    control={updateOrganizationForm.control}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                <Entity namespace={nsOrganization} entity='keys.billing_id_read' />
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    type='text'
                                                    autoComplete='off'
                                                    disabled={!getBillingIdUpdateField()}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            }
                            <FormField
                                name='comment'
                                control={updateOrganizationForm.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            <Entity namespace={nsOrganization} entity='keys.comment' />
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                className='h-8'
                                                type='textarea'
                                                autoComplete='off'
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </Form >
                </ModalBody>
                <FooterForm
                    onSubmit={
                        () => {
                            const organizationDataToSend = { ...organizationDetails, ...updateOrganizationForm.getValues() };
                            updateOrganization.mutate({
                                organizationId: organizationId,
                                organizationData: organizationDataToSend,
                                shouldBlockNewPOS: organizationDetails[organizationFields.SHOULD_BLOCK_NEW_POS.key]
                            });
                        }
                    }
                    isSubmitting={updateOrganization.isLoading}
                    isSubmitDisabled={!updateOrganizationForm.formState.isValid}
                />
            </ModalContent>
        </Modal>
    );
}

ModalUpdateOrganization.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    organizationDetails: PropTypes.object.isRequired,
    setLaunchRefetch: PropTypes.func
};

export default ModalUpdateOrganization;